/**
 * Promise polyfills for the React Native (Hermes) runtime.
 *
 * React Native installs its own JavaScript `Promise` (from `@react-native/js-polyfills`,
 * based on the `promise` package) which — unlike a modern browser/Node engine —
 * does NOT implement `Promise.allSettled` or `Promise.any`. Under Hermes those
 * properties are therefore `undefined`, and calling them throws the opaque
 * release-only crash:
 *
 *     TypeError: undefined is not a function, js engine: hermes
 *
 * which is exactly what took down `AppNavigator.jsx` (the screen-prefetch effect
 * called `Promise.allSettled([...])` raw). Several screens already guard the call
 * by hand (e.g. `WarehousePickingScreen`), but a single global polyfill loaded at
 * the very top of the entry point makes EVERY current and future call site safe on
 * any Android device, without scattering `typeof Promise.allSettled === 'function'`
 * checks through the app.
 *
 * NOTE: On Hermes, `react-native` keeps the engine's NATIVE `Promise` intrinsic
 * (see `Libraries/Core/polyfillPromise.js` — it only swaps in the `promise`
 * package when Hermes is absent). On the Hermes build shipped with this app that
 * native Promise lacks `allSettled`/`any`, and a plain assignment onto the
 * intrinsic does not reliably stick — so call sites must not depend on this
 * global patch alone. It is kept as defence-in-depth, but hot paths that prefetch
 * or fan out (e.g. `AppNavigator.jsx`) settle their promises locally instead.
 *
 * Each polyfill is installed only when missing, so on engines that already provide
 * the spec method (newer Hermes / web) the native implementation is kept.
 */

/* eslint-disable no-undef */

/**
 * Spec-compatible `Promise.allSettled` implemented on top of `Promise.all` +
 * `Promise.prototype.then` ONLY — both of which Hermes always provides. This is
 * the function every call site should import and use directly (see `settleAll`),
 * so it can never hit the "does not reliably stick" intrinsic problem above.
 *
 * @param {Iterable<*>} iterable - Promises (or plain values) to settle.
 * @returns {Promise<Array<{status:string, value?:*, reason?:*}>>}
 */
function allSettledImpl(iterable) {
    return Promise.all(
        Array.from(iterable, (item) =>
            Promise.resolve(item).then(
                (value) => ({status: 'fulfilled', value}),
                (reason) => ({status: 'rejected', reason})
            )
        )
    );
}

/**
 * Spec-compatible `Promise.any`: resolves with the first fulfilled value, or
 * rejects with an `AggregateError` if every input rejects. Uses only
 * `Promise.prototype.then`, so it is safe on the Hermes build this app ships.
 *
 * @param {Iterable<*>} iterable - Promises (or plain values) to race for success.
 * @returns {Promise<*>}
 */
function anyImpl(iterable) {
    const items = Array.from(iterable);

    return new Promise((resolve, reject) => {
        let pending = items.length;
        const errors = new Array(pending);

        if (pending === 0) {
            const aggregate =
                typeof AggregateError === 'function'
                    ? new AggregateError([], 'All promises were rejected')
                    : Object.assign(new Error('All promises were rejected'), {errors: []});
            reject(aggregate);
            return;
        }

        items.forEach((item, index) => {
            Promise.resolve(item).then(resolve, (error) => {
                errors[index] = error;
                pending -= 1;
                if (pending === 0) {
                    const aggregate =
                        typeof AggregateError === 'function'
                            ? new AggregateError(errors, 'All promises were rejected')
                            : Object.assign(new Error('All promises were rejected'), {errors});
                    reject(aggregate);
                }
            });
        });
    });
}

/**
 * Force `fn` onto the `Promise` constructor under `name`. Plain assignment onto
 * the Hermes intrinsic can be silently dropped when the slot is non-writable, so
 * fall back to `Object.defineProperty` (which succeeds whenever the slot is at
 * least configurable). Wrapped in try/catch: a fully locked intrinsic must never
 * throw at bundle-eval time — the exported helpers below are the real guarantee.
 *
 * @param {string} name - Static method name (`allSettled` / `any`).
 * @param {Function} fn - Implementation to install.
 * @returns {void}
 */
function installStatic(name, fn) {
    if (typeof Promise[name] === 'function') {
        return;
    }
    try {
        Promise[name] = fn;
        if (typeof Promise[name] === 'function') {
            return;
        }
        Object.defineProperty(Promise, name, {
            value: fn,
            writable: true,
            configurable: true,
        });
    } catch (_error) {
        // Intrinsic is fully locked — call sites use the exported helpers instead.
    }
}

installStatic('allSettled', allSettledImpl);
installStatic('any', anyImpl);

/**
 * Resilient `Promise.allSettled`: prefers the engine's native implementation and
 * otherwise uses the local one. Import and call this in boot/render hot paths
 * (App.jsx boot, widget snapshot, …) instead of `Promise.allSettled` so a
 * non-sticking intrinsic can never surface the release-only
 * "undefined is not a function, js engine: hermes" crash.
 *
 * @param {Iterable<*>} iterable - Promises (or plain values) to settle.
 * @returns {Promise<Array<{status:string, value?:*, reason?:*}>>}
 */
export function settleAll(iterable) {
    return typeof Promise.allSettled === 'function'
        ? Promise.allSettled(iterable)
        : allSettledImpl(iterable);
}

/**
 * Resilient `Promise.any` — native when available, local fallback otherwise.
 *
 * @param {Iterable<*>} iterable - Promises (or plain values) to race for success.
 * @returns {Promise<*>}
 */
export function anyOf(iterable) {
    return typeof Promise.any === 'function'
        ? Promise.any(iterable)
        : anyImpl(iterable);
}
